import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { mockData } from '../mock';
import { useAuth } from '../context/AuthContext';

const Header = ({ onRequestQuote }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: 'Home', id: 'home' },
    { label: 'About', id: 'about' },
    { label: 'Products', id: 'products' },
    { label: 'Technology', id: 'technology' },
    { label: 'Gallery', id: 'gallery' },
    { label: 'Contact', id: 'contact' }
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-emerald-800 text-white py-2 px-4 text-sm hidden md:block">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href={`mailto:${mockData.contact.email}`} className="flex items-center gap-2 hover:text-emerald-200 transition-colors">
              <Mail size={14} />
              <span>{mockData.contact.email}</span>
            </a>
            <a href={`tel:${mockData.contact.phone}`} className="flex items-center gap-2 hover:text-emerald-200 transition-colors">
              <Phone size={14} />
              <span>{mockData.contact.phone}</span>
            </a>
          </div>
          <div className="text-emerald-100">
            Established {mockData.company.established} | ISO Certified
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`fixed w-full top-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-lg shadow-lg mt-0' 
            : 'bg-white/90 backdrop-blur-md mt-0 md:mt-10'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="/logo.jpeg" 
                alt="Four Pillars Global LLP" 
                className="h-14 w-auto object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900 leading-tight">
                  Eggfy
                </h1>
                <p className="text-xs text-emerald-700 font-medium">by Four Pillars Global</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-gray-700 hover:text-emerald-700 font-medium transition-colors relative group"
                >
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-700 transition-all group-hover:w-full"></span>
                </button>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              {user ? (
                <button
                  data-testid="header-account-btn"
                  onClick={() => navigate('/account')}
                  className="flex items-center gap-2 text-gray-700 hover:text-emerald-700 font-medium transition-colors"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="h-8 w-8 rounded-full object-cover border border-emerald-200" />
                  ) : (
                    <span className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-700">
                      {user.name?.charAt(0)}
                    </span>
                  )}
                  <span>{user.name?.split(' ')[0]}</span>
                </button>
              ) : (
                <Button
                  data-testid="header-signin-btn"
                  variant="outline"
                  onClick={login}
                  className="border-emerald-700 text-emerald-700 hover:bg-emerald-50"
                >
                  Sign In
                </Button>
              )}
              <Button 
                onClick={onRequestQuote}
                className="bg-emerald-700 hover:bg-emerald-800 text-white px-6"
              >
                Request Quote
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-gray-700 hover:text-emerald-700 transition-colors"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-gray-700 hover:text-emerald-700 font-medium py-2 transition-colors"
                >
                  {item.label}
                </button>
              ))}
              {user ? (
                <Button
                  data-testid="mobile-account-btn"
                  variant="outline"
                  onClick={() => navigate('/account')}
                  className="border-emerald-700 text-emerald-700 w-full"
                >
                  My Account ({user.name?.split(' ')[0]})
                </Button>
              ) : (
                <Button
                  data-testid="mobile-signin-btn"
                  variant="outline"
                  onClick={login}
                  className="border-emerald-700 text-emerald-700 w-full"
                >
                  Sign In
                </Button>
              )}
              <Button 
                onClick={() => {
                  onRequestQuote();
                  setIsMobileMenuOpen(false);
                }}
                className="bg-emerald-700 hover:bg-emerald-800 text-white w-full"
              >
                Request Quote
              </Button>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
